"use client";

import { createContext, useCallback, useContext } from "react";
import {
  permalinkPath,
  type PermalinkPageType,
  type Permalinks,
} from "@tripcart/shared";

const PermalinksContext = createContext<Permalinks | undefined>(undefined);

/**
 * Seeds the agency's permalink words for client links (feature: permalinks,
 * AC-04) — the mirror of `DateTimeFormatProvider`. Resolved server-side and
 * passed as a plain prop; absent words read as the defaults.
 */
export function PermalinksProvider({
  permalinks,
  children,
}: {
  permalinks: Permalinks | undefined;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <PermalinksContext.Provider value={permalinks}>
      {children}
    </PermalinksContext.Provider>
  );
}

export function usePermalinks(): Permalinks | undefined {
  return useContext(PermalinksContext);
}

/** Returns a `permalinkPath` bound to the agency's words: `path("tour", slug)`. */
export function usePermalinkPath(): (
  type: PermalinkPageType,
  ...rest: string[]
) => string {
  const permalinks = usePermalinks();
  return useCallback(
    (type, ...rest) => permalinkPath(permalinks, type, ...rest),
    [permalinks],
  );
}
